import React, { useState } from 'react';

// Small multi-series SVG line chart. series: [{ label, color, values: [number|null] }],
// labels: x-axis labels (one per point), hoverLabels: optional fuller text per point
// shown on hover, marker: index of a vertical "from here" line.
export default function LineChart({ series, labels, hoverLabels, height = 180, suffix = '', marker = null, markerLabel = '' }) {
  const [hover, setHover] = useState(null);
  const W = 640, H = height, PAD = { top: 12, right: 12, bottom: 22, left: 36 };
  const n = labels.length;
  const all = series.reduce((acc, s) => acc.concat(s.values.filter(v => v != null)), []);
  if (!n || !all.length) {
    return <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>אין עדיין נתונים</div>;
  }
  const max = Math.max.apply(null, all.concat(1));
  const top = suffix === '%' ? Math.min(100, Math.ceil(max / 10) * 10 || 10) : Math.ceil(max * 1.1);
  const x = i => PAD.left + (n === 1 ? (W - PAD.left - PAD.right) / 2 : i * (W - PAD.left - PAD.right) / (n - 1));
  const y = v => PAD.top + (1 - v / top) * (H - PAD.top - PAD.bottom);
  const every = Math.max(1, Math.ceil(n / 8));

  return (
    <div dir="ltr">
      <svg viewBox={'0 0 ' + W + ' ' + H} style={{ width: '100%', height: 'auto', display: 'block' }}
        onMouseLeave={() => setHover(null)}
        onMouseMove={e => {
          const box = e.currentTarget.getBoundingClientRect();
          const px = (e.clientX - box.left) / box.width * W;
          let best = 0;
          for (let i = 1; i < n; i++) if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i;
          setHover(best);
        }}>
        {[0, 0.5, 1].map(f => (
          <g key={f}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(top * f)} y2={y(top * f)} stroke="var(--border)" strokeWidth="1" />
            <text x={PAD.left - 6} y={y(top * f) + 3} textAnchor="end" fontSize="10" fill="var(--text-muted)">{Math.round(top * f) + suffix}</text>
          </g>
        ))}
        {marker != null && marker >= 0 && marker < n && (
          <g>
            <line x1={x(marker)} x2={x(marker)} y1={PAD.top} y2={H - PAD.bottom} stroke="var(--yellow)" strokeWidth="1" strokeDasharray="4 3" />
            <text x={x(marker) + 4} y={PAD.top + 8} fontSize="10" fill="var(--yellow)">{markerLabel}</text>
          </g>
        )}
        {labels.map((l, i) => ((i % every === 0 && n - 1 - i >= every) || i === n - 1) && (
          <text key={i} x={x(i)} y={H - 6} fontSize="10" fill="var(--text-muted)"
            textAnchor={n > 1 && i === n - 1 ? 'end' : n > 1 && i === 0 ? 'start' : 'middle'}>{l}</text>
        ))}
        {series.map(s => {
          const pts = s.values.map((v, i) => (v == null ? null : [x(i), y(v)])).filter(Boolean);
          return (
            <g key={s.label}>
              {pts.length > 1 && <path d={'M' + pts.map(p => p.join(',')).join(' L')} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" />}
              {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r={n > 30 ? 0 : 3} fill={s.color} />)}
            </g>
          );
        })}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom} stroke="var(--border-light)" strokeWidth="1" />}
      </svg>
      <div dir="rtl" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, minHeight: 16 }}>
        {hover != null && <span style={{ fontWeight: 600, color: 'var(--text)' }}>{(hoverLabels || labels)[hover]}</span>}
        {series.map(s => (
          <span key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 3, background: s.color, display: 'inline-block' }} />
            {s.label}{hover != null && s.values[hover] != null ? ': ' + s.values[hover] + suffix : ''}
          </span>
        ))}
      </div>
    </div>
  );
}
